/**
 * Centralized API configuration
 * All API calls should use this configuration
 */

/**
 * Normalize URL for production:
 * - Ensure https:// protocol for non-localhost
 * - Strip port numbers for production (uses standard ports)
 */
export function normalizeBackendUrl(url: string | undefined): string {
  if (!url) return "";

  let normalized = url.trim();

  // For localhost/127.0.0.1, keep as-is (development)
  if (normalized.includes("localhost") || normalized.includes("127.0.0.1")) {
    return normalized;
  }

  // Force https:// for production URLs
  normalized = normalized.replace(/^http:\/\//, "https://");
  if (!normalized.startsWith("https://")) {
    normalized = "https://" + normalized.replace(/^\/\//, "");
  }

  // Remove port numbers for production (standard ports 80/443)
  normalized = normalized.replace(/:\d+/, "");

  return normalized;
}

/**
 * Get the backend API base URL (for server-side routes)
 * Uses INTERNAL_BACKEND_URL for Docker internal communication, falls back to public URL
 */
export function getInternalBackendUrl(): string {
  const internal = process.env.INTERNAL_BACKEND_URL;
  if (internal) {
    return internal; // Internal Docker URL - no normalization needed
  }
  return normalizeBackendUrl(process.env.NEXT_PUBLIC_BACKEND_URL);
}

/**
 * Get the backend API base URL (for client-side)
 */
export function getApiUrl(): string {
  const url = process.env.NEXT_PUBLIC_BACKEND_URL;
  if (!url) {
    throw new Error("NEXT_PUBLIC_BACKEND_URL environment variable is not set");
  }
  return normalizeBackendUrl(url);
}

/**
 * Build a full API endpoint URL
 */
export function buildApiUrl(path: string): string {
  const baseUrl = getApiUrl();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Common API endpoints
 */
export const API_ENDPOINTS = {
  // Connections
  connections: () => buildApiUrl("/v1/connections/"),
  connection: (id: string) => buildApiUrl(`/v1/connections/${id}`),
  connectionTest: (id: string) => buildApiUrl(`/v1/connections/${id}/test`),
  connectionTestEndpoint: (id: string) =>
    buildApiUrl(`/v1/connections/${id}/test-endpoint`),
  connectionMemories: (id: string, memoryType?: string) =>
    buildApiUrl(
      `/v1/connections/${id}/memories${
        memoryType ? `?memory_type=${memoryType}` : ""
      }`
    ),
  connectionErrors: (id: string) => buildApiUrl(`/v1/connections/${id}/errors`),
  connectionDiscoverEndpoints: (id: string) =>
    buildApiUrl(`/v1/connections/${id}/discover-endpoints`),
  connectionUploadSpec: (connectionId?: string) =>
    buildApiUrl(
      `/v1/connections/upload-spec${connectionId ? `?connection_id=${encodeURIComponent(connectionId)}` : ""}`
    ),

  // Connection access control (sharing)
  connectionAccessUsers: (id: string) =>
    buildApiUrl(`/v1/access-control/connections/${id}/users`),
  connectionAccessGrant: (id: string) =>
    buildApiUrl(`/v1/access-control/connections/${id}/grant`),
  connectionAccessGrantAll: (id: string) =>
    buildApiUrl(`/v1/access-control/connections/${id}/grant-all`),
  connectionAccessRevokeAll: (id: string) =>
    buildApiUrl(`/v1/access-control/connections/${id}/revoke-all`),
  connectionAccessRevoke: (id: string, userId: string) =>
    buildApiUrl(
      `/v1/access-control/connections/${id}/revoke/${encodeURIComponent(userId)}`
    ),
  accessControlUserSearch: (query: string) =>
    buildApiUrl(
      `/v1/access-control/users/search?query=${encodeURIComponent(query)}`
    ),

  // Data Spaces
  dataSpaces: () => buildApiUrl("/v1/data-spaces/"),
  dataSpace: (id: string) => buildApiUrl(`/v1/data-spaces/${id}`),
  dataSpaceEndpoints: (id: string) =>
    buildApiUrl(`/v1/data-spaces/${id}/endpoints`),
  dataSpaceEndpoint: (spaceId: string, endpointId: string) =>
    buildApiUrl(`/v1/data-spaces/${spaceId}/endpoints/${endpointId}`),
  dataSpaceVirtualEndpoints: (id: string) =>
    buildApiUrl(`/v1/data-spaces/${id}/virtual-endpoints`),
  dataSpaceVirtualEndpoint: (spaceId: string, virtualId: string) =>
    buildApiUrl(`/v1/data-spaces/${spaceId}/virtual-endpoints/${virtualId}`),
  dataSpaceVirtualEndpointExecute: (spaceId: string, virtualId: string) =>
    buildApiUrl(
      `/v1/data-spaces/${spaceId}/virtual-endpoints/${virtualId}/execute`
    ),
  dataSpaceRelationships: (id: string) =>
    buildApiUrl(`/v1/data-spaces/${id}/relationships`),
  dataSpaceRelationshipsDetect: (id: string) =>
    buildApiUrl(`/v1/data-spaces/${id}/relationships/detect`),
  dataSpaceRelationship: (spaceId: string, relationshipId: string) =>
    buildApiUrl(`/v1/data-spaces/${spaceId}/relationships/${relationshipId}`),

  // Data Space Creation
  dataSpaceDesign: () => buildApiUrl("/v1/data-space-creation/design"),
  dataSpaceQuickCreate: () =>
    buildApiUrl("/v1/data-space-creation/quick-create"),
  dataSpaceCreate: () => buildApiUrl("/v1/data-space-creation/create"),
  dataSpacePreview: () => buildApiUrl("/v1/data-space-creation/preview"),

  // Data Analyst
  dataAnalystAnalyses: (connectionId: string) =>
    buildApiUrl(`/v1/data-analyst/${connectionId}/analyses`),
  dataAnalystSummary: (connectionId: string) =>
    buildApiUrl(`/v1/data-analyst/${connectionId}/summary`),
  dataAnalystAnalyzeEndpoint: (connectionId: string) =>
    buildApiUrl(`/v1/data-analyst/${connectionId}/analyze-endpoint`),
  dataAnalystAnalyzeAll: (connectionId: string) =>
    buildApiUrl(`/v1/data-analyst/${connectionId}/analyze-all`),
  dataAnalystAnalysis: (connectionId: string, analysisId: string) =>
    buildApiUrl(`/v1/data-analyst/${connectionId}/analysis/${analysisId}`),

  // Data Chatter
  dataChatterSuggestParameters: () =>
    buildApiUrl("/v1/data-chatter/suggest-parameters"),
};
